import { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, MapPin, Building, Calendar, Trash2 } from 'lucide-react';
import { getAdminReservations, getReservationStats, deleteAdminReservation } from '../lib/api';
import { MachineReservation } from '../types/map';
import { useToast } from '../components/ui/use-toast';
import { Button } from '../components/ui/button';
import { ReservationReviewModal } from '../components/ReservationReviewModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

type TabType = 'all' | 'pending' | 'approved' | 'denied' | 'duplicates';

export default function AdminReservations() {
  const [reservations, setReservations] = useState<MachineReservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('pending');
  const [selectedReservation, setSelectedReservation] = useState<MachineReservation | null>(null);
  const [reservationToDelete, setReservationToDelete] = useState<MachineReservation | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [stats, setStats] = useState({ pending: 0, approved: 0, denied: 0, total: 0 });
  const { toast } = useToast();

  useEffect(() => {
    loadData();
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const res = await fetch('/auth/me', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const roles: string[] = data.roles || [];
        setIsSuperAdmin(roles.includes('superadmin'));
      }
    } catch (error) {
      console.error('Error checking super admin status:', error);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [reservationsData, statsData] = await Promise.all([
        getAdminReservations(),
        getReservationStats(),
      ]);
      setReservations(reservationsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading reservations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load reservations',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSuccess = () => {
    setSelectedReservation(null);
    loadData();
  };

  const handleDeleteClick = (reservation: MachineReservation) => {
    setReservationToDelete(reservation);
  };

  const handleDeleteConfirm = async () => {
    if (!reservationToDelete) return;

    try {
      setIsDeleting(true);
      const result = await deleteAdminReservation(reservationToDelete.id);
      
      toast({
        title: 'Success',
        description: result.message || 'Reservation deleted successfully',
      });

      setReservationToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting reservation:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete reservation',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock className="h-4 w-4" />
            Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-4 w-4" />
            Approved
          </span>
        );
      case 'denied':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="h-4 w-4" />
            Denied
          </span>
        );
      default:
        return null;
    }
  };

  // Identify duplicate requests (same machine + brand)
  const getDuplicateKey = (reservation: MachineReservation) => {
    return `${reservation.machineId}-${reservation.brandId}`;
  };

  const duplicateGroups = reservations.reduce((acc, reservation) => {
    const key = getDuplicateKey(reservation);
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(reservation);
    return acc;
  }, {} as Record<string, MachineReservation[]>);

  const duplicateReservations = Object.values(duplicateGroups)
    .filter(group => group.length > 1)
    .flat()
    .filter(r => r.status === 'pending'); // Only show pending duplicates

  const filteredReservations = reservations.filter(r => {
    if (activeTab === 'all') return true;
    if (activeTab === 'duplicates') return false; // Handled separately
    return r.status === activeTab;
  });

  const sortedReservations = [...filteredReservations].sort((a, b) => {
    // Pending first, then by requested date
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    return new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime();
  });

  return (
    <div className="h-full bg-gray-50">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Reservation Requests</h1>
          <p className="text-gray-600 mt-2">
            Review and manage machine reservation requests from brands
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <div className="text-sm text-gray-600">Total Requests</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-yellow-50 rounded-lg border border-yellow-200 p-4">
            <div className="text-sm text-yellow-800">Pending Review</div>
            <div className="text-2xl font-bold text-yellow-900">{stats.pending}</div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="text-sm text-green-800">Approved</div>
            <div className="text-2xl font-bold text-green-900">{stats.approved}</div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="text-sm text-red-800">Denied</div>
            <div className="text-2xl font-bold text-red-900">{stats.denied}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg border mb-6">
          <div className="flex border-b overflow-x-auto">
            {(['pending', 'all', 'approved', 'denied', 'duplicates'] as TabType[]).map(tab => {
              const count = tab === 'all' 
                ? stats.total 
                : tab === 'duplicates'
                ? duplicateReservations.length
                : stats[tab as keyof typeof stats];
              
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-3 text-sm font-medium capitalize transition-colors whitespace-nowrap ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab === 'duplicates' ? 'Duplicate Requests' : tab}
                  <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Reservations List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading reservations...</p>
          </div>
        ) : activeTab === 'duplicates' ? (
          duplicateReservations.length === 0 ? (
            <div className="bg-white rounded-lg border p-12 text-center">
              <div className="text-gray-400 mb-4">
                <Calendar className="h-16 w-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Duplicate Requests
              </h3>
              <p className="text-gray-600">
                No duplicate pending requests found at this time.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(duplicateGroups)
                .filter(([_, group]) => group.length > 1 && group.some(r => r.status === 'pending'))
                .map(([key, group]) => {
                  const pendingGroup = group.filter(r => r.status === 'pending');
                  const machine = group[0].Machine;
                  const brand = group[0].Brand;
                  
                  return (
                    <div key={key} className="bg-white rounded-lg border p-6">
                      <div className="mb-4 pb-4 border-b">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                            {pendingGroup.length} Duplicate Request{pendingGroup.length !== 1 ? 's' : ''}
                          </span>
                          <span className="text-sm text-gray-600">
                            {brand?.name || 'Unknown Brand'} • {machine?.name || 'Unknown Machine'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          Multiple pending requests for the same machine from the same brand
                        </p>
                      </div>
                      <div className="space-y-3">
                        {pendingGroup.map(reservation => (
                          <div key={reservation.id} className="bg-gray-50 rounded-lg border p-4">
                            {(() => {
                              const reservationComponent = (
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                      {getStatusBadge(reservation.status)}
                                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Clock className="h-4 w-4" />
                                        <span>Requested {reservation.detailedInfo ? formatDateTime(reservation.detailedInfo.requestedAt) : formatDate(reservation.requestedAt)}</span>
                                      </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                      <div>
                                        <div className="text-xs text-gray-500 mb-1">Requested By</div>
                                        <div className="font-medium text-gray-900">
                                          {reservation.requester?.firstName || reservation.requester?.lastName
                                            ? `${reservation.requester.firstName || ''} ${reservation.requester.lastName || ''}`.trim()
                                            : reservation.requester?.email || 'Unknown User'}
                                        </div>
                                        {reservation.requester?.email && (reservation.requester?.firstName || reservation.requester?.lastName) && (
                                          <div className="text-xs text-gray-500 mt-0.5">{reservation.requester.email}</div>
                                        )}
                                      </div>
                                      <div>
                                        <div className="text-xs text-gray-500 mb-1">Reservation Type</div>
                                        <div className="font-medium text-gray-900">
                                          {reservation.reservationType || 'Standard'}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Enhanced detailed information for duplicate pending requests */}
                                    {reservation.detailedInfo && (
                                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-3 mt-3 text-sm">
                                        <div className="grid grid-cols-3 gap-2 mb-2">
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className="text-xs text-gray-500">Machines</div>
                                            <div className="text-lg font-bold text-blue-900">{reservation.detailedInfo.machineCount}</div>
                                          </div>
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className="text-xs text-gray-500">SKUs</div>
                                            <div className="text-lg font-bold text-blue-900">{reservation.detailedInfo.skuCount}</div>
                                          </div>
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className="text-xs text-gray-500">Retail Media</div>
                                            <div className="text-lg font-bold text-blue-900">{reservation.detailedInfo.retailMediaCount}</div>
                                          </div>
                                        </div>
                                        
                                        {reservation.detailedInfo.costs.total.monthly > 0 && (
                                          <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                                            <div className="text-xs text-gray-600">Monthly: £{reservation.detailedInfo.costs.total.monthly.toFixed(2)}</div>
                                            <div className="text-xs text-gray-600">12 Month: £{reservation.detailedInfo.costs.total.yearly.toFixed(2)}</div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {reservation.Machine?.requiresTenderProcess && reservation.MachineSKUAllocations && reservation.MachineSKUAllocations.length > 0 && !reservation.detailedInfo && (() => {
                                      let planogramConfig: any = null;
                                      try {
                                        const config = reservation.Machine?.planogramConfig;
                                        if (config) {
                                          planogramConfig = typeof config === 'string' ? JSON.parse(config) : config;
                                        }
                                      } catch (e) {
                                        console.error('Error parsing planogram config:', e);
                                      }

                                      const categoryCounts: Record<string, number> = {};
                                      reservation.MachineSKUAllocations.forEach((alloc) => {
                                        if (planogramConfig?.positions) {
                                          const position = planogramConfig.positions.find(
                                            (p: any) => p.row === alloc.rowIndex && p.col === alloc.columnIndex
                                          );
                                          const category = position?.category || 'Unknown';
                                          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                                        } else {
                                          categoryCounts['Unknown'] = (categoryCounts['Unknown'] || 0) + 1;
                                        }
                                      });

                                      return (
                                        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mt-3 text-sm">
                                          <div className="font-medium text-purple-900 mb-2">SKU Selections</div>
                                          <div className="flex flex-wrap gap-2 mb-2">
                                            {Object.entries(categoryCounts).map(([category, count]) => (
                                              <span key={category} className="inline-flex items-center px-2 py-1 bg-purple-200 text-purple-900 rounded text-xs font-medium">
                                                {count} {category}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {(() => {
                                      if (!reservation.notes) return null;
                                      
                                      try {
                                        const data = JSON.parse(reservation.notes);
                                        if (data.venueSelections || data.retailMediaSelections || data.reportingTier) {
                                          return null;
                                        }
                                        return (
                                          <div className="bg-gray-50 rounded-md p-3 mt-3 text-sm">
                                            <div className="font-medium text-gray-700 mb-1">Request Notes</div>
                                            <div className="text-gray-600">{reservation.notes}</div>
                                          </div>
                                        );
                                      } catch {
                                        return (
                                          <div className="bg-gray-50 rounded-md p-3 mt-3 text-sm">
                                            <div className="font-medium text-gray-700 mb-1">Request Notes</div>
                                            <div className="text-gray-600">{reservation.notes}</div>
                                          </div>
                                        );
                                      }
                                    })()}
                                  </div>
                                  <div className="ml-4 flex flex-col gap-2">
                                    <Button
                                      onClick={() => setSelectedReservation(reservation)}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      Review
                                    </Button>
                                    {isSuperAdmin && (
                                      <Button
                                        onClick={() => handleDeleteClick(reservation)}
                                        variant="destructive"
                                        size="sm"
                                      >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        Delete
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                              return reservationComponent;
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          )
        ) : sortedReservations.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <div className="text-gray-400 mb-4">
              <Calendar className="h-16 w-16 mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {activeTab !== 'all' ? activeTab : ''} reservations
            </h3>
            <p className="text-gray-600">
              {activeTab === 'pending' 
                ? "No pending requests to review at this time."
                : `No ${activeTab} requests found.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedReservations.map(reservation => (
              <div key={reservation.id} className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusBadge(reservation.status)}
                      <div className="flex items-center gap-1.5 text-sm text-gray-600">
                        <Clock className="h-4 w-4" />
                        <span>Requested {reservation.detailedInfo ? formatDateTime(reservation.detailedInfo.requestedAt) : formatDate(reservation.requestedAt)}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-start gap-2 mb-2">
                          <Building className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <div className="text-xs text-gray-500">Brand</div>
                            <div className="font-semibold text-gray-900">
                              {reservation.Brand?.name || 'Unknown Brand'}
                            </div>
                          </div>
                        </div>
                        {reservation.requester && (
                          <div className="flex items-center gap-2 ml-7 mt-1">
                            <div className="text-xs text-gray-500">Requested by:</div>
                            <div className="text-sm font-medium text-gray-900">
                              {reservation.requester.firstName || reservation.requester.lastName
                                ? `${reservation.requester.firstName || ''} ${reservation.requester.lastName || ''}`.trim()
                                : reservation.requester.email || 'Unknown User'}
                            </div>
                          </div>
                        )}
                        {(reservation.requester as any)?.creator && (
                          <div className="text-xs text-blue-600 font-medium ml-7 mt-1">
                            👤 Admin: {(reservation.requester as any).creator.firstName} {(reservation.requester as any).creator.lastName}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <div className="text-xs text-gray-500">Machine</div>
                            <div className="font-semibold text-gray-900">
                              {reservation.Machine?.name || 'Unknown Machine'}
                            </div>
                            <div className="text-xs text-gray-600 mt-0.5">
                              {reservation.Machine?.venueType}
                              {reservation.Machine?.venueName && ` • ${reservation.Machine.venueName}`}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {reservation.status === 'pending' && (
                      <Button
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        Review Request
                      </Button>
                    )}
                    {isSuperAdmin && (reservation.status === 'pending' || reservation.status === 'approved') && (
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleDeleteClick(reservation)}
                        title="Delete reservation"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {reservation.status === 'approved' && reservation.reservationStart && reservation.reservationEnd && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 mt-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-green-700 font-medium">Start:</span>
                          <span className="text-green-900 ml-2">{formatDate(reservation.reservationStart)}</span>
                        </div>
                        <div>
                          <span className="text-green-700 font-medium">End:</span>
                          <span className="text-green-900 ml-2">{formatDate(reservation.reservationEnd)}</span>
                        </div>
                      </div>
                      {reservation.reviewer && (
                        <div className="text-xs text-green-700">
                          Approved by {reservation.reviewer.firstName} {reservation.reviewer.lastName}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {reservation.status === 'denied' && reservation.denialReason && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 mt-3">
                    <div className="text-sm font-medium text-red-900 mb-1">Reason for Denial</div>
                    <div className="text-sm text-red-800">{reservation.denialReason}</div>
                    {reservation.reviewer && (
                      <div className="text-xs text-red-700 mt-2">
                        Denied by {reservation.reviewer.firstName} {reservation.reviewer.lastName}
                      </div>
                    )}
                  </div>
                )}

                {reservation.reservationType && (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-3 text-sm">
                    <div className="font-medium text-blue-900 mb-1">Reservation Type</div>
                    <div className="text-blue-800">{reservation.reservationType}</div>
                  </div>
                )}

                {/* Enhanced detailed information for pending requests */}
                {reservation.status === 'pending' && reservation.detailedInfo && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-4 mt-3">
                    <div className="font-semibold text-blue-900 mb-3 text-lg">Request Details</div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">Machines</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.machineCount}</div>
                      </div>
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">SKUs Requested</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.skuCount}</div>
                      </div>
                      <div className="bg-white rounded-md p-3 border border-blue-100">
                        <div className="text-xs text-gray-500 mb-1">Retail Media Items</div>
                        <div className="text-2xl font-bold text-blue-900">{reservation.detailedInfo.retailMediaCount}</div>
                      </div>
                    </div>

                    {/* SKU Selections */}
                    {reservation.Machine?.requiresTenderProcess && reservation.MachineSKUAllocations && reservation.MachineSKUAllocations.length > 0 && (() => {
                      // Parse planogram config to get category information
                      let planogramConfig: any = null;
                      try {
                        const config = reservation.Machine?.planogramConfig;
                        if (config) {
                          planogramConfig = typeof config === 'string' ? JSON.parse(config) : config;
                        }
                      } catch (e) {
                        console.error('Error parsing planogram config:', e);
                      }

                      // Group SKU allocations by category
                      const categoryCounts: Record<string, number> = {};
                      reservation.MachineSKUAllocations.forEach((alloc) => {
                        if (planogramConfig?.positions) {
                          const position = planogramConfig.positions.find(
                            (p: any) => p.row === alloc.rowIndex && p.col === alloc.columnIndex
                          );
                          const category = position?.category || 'Unknown';
                          categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                        } else {
                          // Fallback if no planogram config
                          categoryCounts['Unknown'] = (categoryCounts['Unknown'] || 0) + 1;
                        }
                      });

                      return (
                        <div className="bg-purple-50 border border-purple-200 rounded-md p-3 mb-3">
                          <div className="font-medium text-purple-900 mb-2">SKU Selections</div>
                          <div className="flex flex-wrap gap-2 mb-2">
                            {Object.entries(categoryCounts).map(([category, count]) => (
                              <span key={category} className="inline-flex items-center px-3 py-1 bg-purple-200 text-purple-900 rounded text-sm font-medium">
                                {count} {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Retail Media Selections */}
                    {reservation.detailedInfo.costs.retailMediaCosts.breakdown.length > 0 && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-3">
                        <div className="font-medium text-indigo-900 mb-2">Retail Media Listings</div>
                        <div className="space-y-2">
                          {reservation.detailedInfo.costs.retailMediaCosts.breakdown.map((item) => (
                            <div key={item.itemId} className="bg-white rounded p-2 text-sm">
                              <div className="font-medium text-indigo-900">{item.itemName}</div>
                              <div className="text-xs text-gray-600">
                                {item.machines} machine{item.machines !== 1 ? 's' : ''} × {item.units} unit{item.units !== 1 ? 's' : ''}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cost Summary */}
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                      <div className="font-semibold text-green-900 mb-3">Cost Summary</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Monthly Costs</div>
                          {reservation.detailedInfo.costs.skuCosts.monthly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              SKUs: £{reservation.detailedInfo.costs.skuCosts.monthly.toFixed(2)}
                            </div>
                          )}
                          {reservation.detailedInfo.costs.retailMediaCosts.monthly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              Retail Media: £{reservation.detailedInfo.costs.retailMediaCosts.monthly.toFixed(2)}
                            </div>
                          )}
                          <div className="text-lg font-bold text-green-900 mt-2 pt-2 border-t border-green-300">
                            Total Monthly: £{reservation.detailedInfo.costs.total.monthly.toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 mb-1">12 Month Costs</div>
                          {reservation.detailedInfo.costs.skuCosts.yearly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              SKUs: £{reservation.detailedInfo.costs.skuCosts.yearly.toFixed(2)}
                            </div>
                          )}
                          {reservation.detailedInfo.costs.retailMediaCosts.yearly > 0 && (
                            <div className="text-sm text-gray-700 mb-1">
                              Retail Media: £{reservation.detailedInfo.costs.retailMediaCosts.yearly.toFixed(2)}
                            </div>
                          )}
                          <div className="text-lg font-bold text-green-900 mt-2 pt-2 border-t border-green-300">
                            Total 12 Month: £{reservation.detailedInfo.costs.total.yearly.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fallback SKU display for non-pending or when detailedInfo not available */}
                {reservation.status !== 'pending' && reservation.Machine?.requiresTenderProcess && reservation.MachineSKUAllocations && reservation.MachineSKUAllocations.length > 0 && (() => {
                  // Parse planogram config to get category information
                  let planogramConfig: any = null;
                  try {
                    const config = reservation.Machine?.planogramConfig;
                    if (config) {
                      planogramConfig = typeof config === 'string' ? JSON.parse(config) : config;
                    }
                  } catch (e) {
                    console.error('Error parsing planogram config:', e);
                  }

                  // Group SKU allocations by category
                  const categoryCounts: Record<string, number> = {};
                  reservation.MachineSKUAllocations.forEach((alloc) => {
                    if (planogramConfig?.positions) {
                      const position = planogramConfig.positions.find(
                        (p: any) => p.row === alloc.rowIndex && p.col === alloc.columnIndex
                      );
                      const category = position?.category || 'Unknown';
                      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
                    } else {
                      // Fallback if no planogram config
                      categoryCounts['Unknown'] = (categoryCounts['Unknown'] || 0) + 1;
                    }
                  });

                  const totalSKUs = reservation.MachineSKUAllocations.length;

                  return (
                    <div className="bg-purple-50 border border-purple-200 rounded-md p-4 mt-3">
                      <div className="font-medium text-purple-900 mb-2">Tender Request - SKU Selections</div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {Object.entries(categoryCounts).map(([category, count]) => (
                          <span key={category} className="inline-flex items-center px-3 py-1 bg-purple-200 text-purple-900 rounded text-sm font-medium">
                            {count} {category}
                          </span>
                        ))}
                      </div>
                      {reservation.Brand?.tenderPricePerSKUPerMonth && (
                        <div className="border-t border-purple-300 pt-2 mt-2">
                          <div className="text-sm text-purple-800">
                            <span className="font-medium">Price per SKU:</span> £{(reservation.Brand.tenderPricePerSKUPerMonth / 100).toFixed(2)}/month
                          </div>
                          <div className="text-lg font-bold text-purple-900 mt-1">
                            Total: £{((reservation.Brand.tenderPricePerSKUPerMonth / 100) * totalSKUs).toFixed(2)}/month
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(() => {
                  // Check if notes is JSON metadata (tender request data) or actual user notes
                  if (!reservation.notes) return null;
                  
                  try {
                    const data = JSON.parse(reservation.notes);
                    // If it's JSON with tender metadata structure, don't show it
                    if (data.venueSelections || data.retailMediaSelections || data.reportingTier) {
                      return null;
                    }
                    // If it's JSON but not our structure, show as-is
                    return (
                      <div className="bg-gray-50 rounded-md p-3 mt-3 text-sm">
                        <div className="font-medium text-gray-700 mb-1">Request Notes</div>
                        <div className="text-gray-600">{reservation.notes}</div>
                      </div>
                    );
                  } catch {
                    // Not JSON, it's actual user notes - show it
                    return (
                      <div className="bg-gray-50 rounded-md p-3 mt-3 text-sm">
                        <div className="font-medium text-gray-700 mb-1">Request Notes</div>
                        <div className="text-gray-600">{reservation.notes}</div>
                      </div>
                    );
                  }
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedReservation && (
        <ReservationReviewModal
          reservation={selectedReservation}
          onClose={() => setSelectedReservation(null)}
          onSuccess={handleReviewSuccess}
        />
      )}

      <AlertDialog open={!!reservationToDelete} onOpenChange={(open) => !open && setReservationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reservation?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this reservation? This action cannot be undone.
              {reservationToDelete?.MachineSKUAllocations && reservationToDelete.MachineSKUAllocations.length > 0 && (
                <span className="block mt-2 font-medium">
                  This will also delete {reservationToDelete.MachineSKUAllocations.length} associated SKU allocation{reservationToDelete.MachineSKUAllocations.length > 1 ? 's' : ''} and free up the planogram positions.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

